/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

// Multibrand-UI Components
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Checkbox from '@zg-rentals/multibrand-ui/lib/Checkbox';
import ArrowDropdown from '@zg-rentals/multibrand-ui/lib/icons/ArrowDropdown';
import CaretDown from '@zg-rentals/multibrand-ui/lib/icons/CaretDown';
import CaretUp from '@zg-rentals/multibrand-ui/lib/icons/CaretUp';
import Filter from '@zg-rentals/multibrand-ui/lib/icons/Filter';

const propTypes = {
    columnConfig: PropTypes.arrayOf(PropTypes.shape({
        width: PropTypes.number.isRequired,
        dataKey: PropTypes.string,
        header: PropTypes.string.isRequired,
        isSortable: PropTypes.bool,
        renderer: PropTypes.func,
        align: PropTypes.string,
        headerRenderer: PropTypes.func,
    })),
    sortKey: PropTypes.object,
    isSelected: PropTypes.bool,
    isFilterOpen: PropTypes.bool,
    canFilter: PropTypes.bool,
    canSelectRow: PropTypes.bool,
    hasExpandableRows: PropTypes.bool.isRequired,
    onSelectToggle: PropTypes.func,
    onFilterToggle: PropTypes.func,
    onSortClick: PropTypes.func,
    isMobile: PropTypes.bool,
    filters: PropTypes.object,
    columnDivisions: PropTypes.number.isRequired,
    selectionDisabled: PropTypes.bool.isRequired,
};

const defaultProps = {
    columnConfig: [],
    sortKey: null,
    isSelected: false,
    isFilterOpen: false,
    canFilter: true,
    canSelectRow: true,
    onSelectToggle: () => { },
    onFilterToggle: () => { },
    onSortClick: () => { },
    isMobile: false,
    filters: {},
};

const TableMFHHeader = ({
    columnConfig,
    sortKey,
    isSelected,
    isFilterOpen,
    canFilter,
    canSelectRow,
    onSelectToggle,
    onFilterToggle,
    onSortClick,
    isMobile,
    filters,
    columnDivisions,
    selectionDisabled,
    hasExpandableRows,
    edgePadding,
}) => {
    const formatValue = col => (col.headerRenderer ? col.headerRenderer() : col.header.toUpperCase());
    const formatColWidth = width => `${width / columnDivisions * 100}%`;
    const FilterDirectionIcon = isFilterOpen ? CaretUp : CaretDown;
    const getSortableClassNames = column => cx('TableMFH-header-icon',
        {
            'TableMFH-current-sort-ascending': sortKey && sortKey.key === column.dataKey && sortKey.ascending,
            'TableMFH-current-sort-descending': sortKey && sortKey.key === column.dataKey && !sortKey.ascending,
            'TableMFH-available-sort': !sortKey || sortKey.key !== column.dataKey,
        });
    const FilterButton = isMobile
        ? (
            <Filter className="Utils-primary TableMFH-header-icon" size={15} />
        ) : (
            <React.Fragment>
                Filter
                <div className="TableMFH-icon-wrapper TableMFH-flex-col">
                    <FilterDirectionIcon className="Utils-primary TableMFH-header-icon" size={12} />
                </div>
            </React.Fragment>
        );
    return (
        <Row
            className="TableMFH-header-row"
            style={{
                paddingLeft: edgePadding,
                paddingRight: edgePadding,
            }}
        >
            {canSelectRow && (
                <div
                    className="TableMFH-col TableMFH-vertical-align"
                    style={{ width: formatColWidth(1) }}
                >
                    <div className="TableMFH-icon-wrapper">
                        <Checkbox
                            checked={isSelected}
                            onChange={onSelectToggle}
                            disabled={selectionDisabled}
                        />
                    </div>
                </div>
            )}
            {columnConfig.map((column, i) => (
                <div
                    style={{ width: formatColWidth(column.width) }}
                    key={`col-header-${i}`}
                    className={cx(
                        'TableMFH-col',
                        {
                            'TableMFH-align-center': column.align === 'center',
                        },
                        {
                            'TableMFH-align-right': column.align === 'right',
                        }
                    )}
                >
                    <div
                        role="button"
                        tabIndex={0}
                        className={cx('TableMFH-header-cell', {
                            'TableMFH-sortable': column.isSortable,
                        })}
                        onClick={() => column.isSortable && onSortClick(column.dataKey)}
                    >
                        <div className="TableMFH-header-cell-inner">
                            {formatValue(column)}
                        </div>
                        {column.isSortable &&
                            (
                                <ArrowDropdown
                                    size={10}
                                    className={getSortableClassNames(column)}
                                />
                            )}
                    </div>
                </div>
            ))}
            {canFilter && (
                <div
                    className="TableMFH-col"
                    style={{ width: formatColWidth(1) }}
                >
                    {Object.values(filters).length > 0 && (
                        <div
                            onClick={onFilterToggle}
                            className="Utils-primary TableMFH-header-cell TableMFH-clickable"
                        >
                            <div className="TableMFH-icon-wrapper">
                                {FilterButton}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {hasExpandableRows && !canFilter && (
                <div
                    className="TableMFH-col"
                    style={{ width: formatColWidth(1) }}
                />
            )}
        </Row>
    );
};

TableMFHHeader.propTypes = propTypes;
TableMFHHeader.defaultProps = defaultProps;

export default TableMFHHeader;
